import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect if the user has zoomed in beyond a certain threshold.
 *
 * Uses a relative zoom ratio (currentDPR / baseDPR) rather than comparing the
 * raw devicePixelRatio against a fixed value. This prevents false positives on
 * Retina/HiDPI screens (e.g. MacBooks) whose hardware DPR is 2× at 100% zoom.
 *
 * @param threshold - The zoom ratio threshold (default: 2, meaning 200% relative zoom)
 * @returns boolean indicating if the user is zoomed in beyond the threshold
 */
export const useZoomDetection = (threshold: number = 2): boolean => {
  // Capture the device's native DPR at first render so Retina screens aren't
  // incorrectly treated as zoomed in.
  const baseDPR = useRef(window.devicePixelRatio);
  const [isZoomedIn, setIsZoomedIn] = useState(false);

  useEffect(() => {
    const checkZoomLevel = () => {
      const zoomRatio = window.devicePixelRatio / baseDPR.current;
      setIsZoomedIn(zoomRatio > threshold);
    };

    checkZoomLevel();
    window.addEventListener('resize', checkZoomLevel);

    return () => {
      window.removeEventListener('resize', checkZoomLevel);
    };
  }, [threshold]);

  return isZoomedIn;
}; 